import { useColorMode } from '@vueuse/core'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import i18n from '@/libs/i18n'
import type { IUser } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const JWT          = ref(localStorage.getItem('JWT') || '')
    const locale       = ref(localStorage.getItem('locale') || 'sl')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const userData     = ref<IUser>()

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Values: 'auto' | 'light' | 'dark'.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    const getJWT         = () => JWT.value
    const isLoggedIn     = () => !!JWT.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    // Sets a JWT cookie so that API requests for images/PDFs loaded via <img src> or
    // <a href> are authenticated — browsers cannot set Authorization headers for those.
    const setJWTCookie = (jwt: string) => {
        if (jwt) {
            document.cookie = `JWT=${jwt}; path=/; SameSite=Lax`
        } else {
            document.cookie = 'JWT=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax'
        }
    }

    setJWTCookie(JWT.value)

    window.addEventListener('resize', () => { windowWidth.value = window.innerWidth })

    const setLogin = (jwt: string) => {
        localStorage.setItem('JWT', jwt)
        JWT.value = jwt
        setJWTCookie(jwt)
    }

    const setLogout = () => {
        localStorage.removeItem('JWT')
        JWT.value = ''
        userData.value = undefined
        setJWTCookie('')
    }

    const setUserData = (user: IUser) => { userData.value = user }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => { loaderActive.value = Math.max(0, loaderActive.value - 1) }

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        i18n.global.locale.value = newLocale as 'sl' | 'en'
    }

    return {
        JWT, locale, userData, loaderActive, colorMode,
        getJWT, isLoggedIn, isLoaderActive, isMobile,
        setLogin, setLogout, setUserData, startLoading, stopLoading, changeLocale,
    }
})
